import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Accordion } from 'components/molecules/Accordion';
import { TableTopButtons } from 'components/atoms/TableTopButtons';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { EPermission } from 'models/permissions';
import { Loader } from 'components/atoms/Loader';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ESortDirectionTypes, ISortOptions } from 'models/table';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { useForm } from 'react-hook-form';
import { useTranslations } from 'hooks/useTranslations';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { renderRowColor, renderRowColorHover } from 'components/molecules/Table/helpers';
import {
  legalEntityCasesTableColumns,
  mapLECasesToDataSource,
} from 'pages/LegalEntitiesPage/helpers';
import {
  CasesSearchForm,
  ISearchCasesFormValues,
  initialCasesSearchFormValues,
} from '../../CasesSearchForm';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { ERouteLinks } from 'models/route';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { LegalEntityCaseTableCard } from 'pages/LegalEntitiesPage/LegalEntityCaseTableCard';
import { ECaseProcessTypes } from 'constants/CaseProcessTypes';
import { usePermission } from 'hooks/usePermission';
import AssignCaseForm from 'pages/CaseManagementPage/CasesPage/CaseDetailsPage/AssignCaseForm';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { IDictionary } from 'models/dictionary';
import { useAccount } from 'pages/AccountPage/hooks';
import { ELegalVisibility } from 'constants/LegalVisibility';

interface ILegalEntityCasesSectionProps {
  id: string;
  type: string;
  isAllExpanded: boolean;
  isDesktop: boolean;
  shortDateFormat: string;
  scrollPosition: React.MutableRefObject<number>;
  legalEntity: ILegalEntityData | null | undefined;
  orgLegalEntity: IOrgLegalEntityData | null | undefined;
  showDetailsCases: boolean;
}
const sortedCaseProcessTypesId = [
  ECaseProcessTypes.APPLICATION,
  ECaseProcessTypes.RENEWAL,
  ECaseProcessTypes.VARIATION,
  ECaseProcessTypes.OTHER_REQUEST,
];

const LegalEntityCasesSection = ({
  id,
  type,
  isAllExpanded,
  isDesktop,
  shortDateFormat,
  scrollPosition,
  legalEntity,
  orgLegalEntity,
  showDetailsCases,
}: ILegalEntityCasesSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { hasPermissions } = usePermission();
  const { canAssignCase } = usePrivilegesValidation();
  const { account, getSelfAccountData } = useAccount();
  const {
    caseProcessTypes,
    getCaseProcessTypes,
    weaponLicenceCaseStatus,
    getWeaponLicenceCaseStatus,
  } = useDictionary();
  const {
    getLegalEntityCasesData,
    legalEntityCases,
    paginatorCases,
    getLegalEntityLicenceAuthoritiesData,
    legalEntityLicenceAuthorities,
  } = useLegalEntities();
  const [casesQuery, setCasesQuery] = useState<Partial<ISearchCasesFormValues>>(
    initialCasesSearchFormValues,
  );
  const [currentPageCases, setCurrentPageCases] = useState<number>(1);
  const [perPageCases, setPerPageCases] = useState<EPerPages>(EPerPages.perPage25);
  const [casesSort, setCasesSort] = useState<ISortOptions | null>({
    property: 'createdDate',
    direction: ESortDirectionTypes.DESC,
  });
  const [isCasePaginationLoading, setIsCasePaginationLoading] = useState(false);
  const [isCaseQueryLoading, setIsCaseQueryLoading] = useState<boolean>(false);
  const [isLegalEntityCasesLoading, setIsLegalEntityCasesLoading] = useState<boolean>(false);
  const [isCasesSortLoaded, setIsCasesSortLoaded] = useState(true);
  const [assignCaseId, setAssignCaseId] = useState<string | null>(null);
  const [caseProcessTypesOptions, setCaseProcessTypesOptions] = useState<IDictionary[]>([]);
  const [canAddCase, setCanAddCase] = useState(false);

  const previousCasesSortRef = useRef(casesSort);

  useEffect(() => {
    getSelfAccountData();
  }, []);

  useEffect(() => {
    (async () => {
      if (id) {
        await getLegalEntityLicenceAuthoritiesData(id);
      }
    })();
  }, [id]);

  useEffect(() => {
    const activeLegalEntity = legalEntity ?? orgLegalEntity;
    const isNonPublicLegal =
      activeLegalEntity?.visibilityUniqueId === ELegalVisibility.NON_PUBLIC;

    if (legalEntityLicenceAuthorities) {
      const includesPrimaryLicenceAuthority = account?.licenceAuthorityIds.includes(
        legalEntityLicenceAuthorities.primaryLicenceAuthorityUniqueId,
      );

      const includesSecondaryLicenceAuthority = account?.licenceAuthorityIds.includes(
        legalEntityLicenceAuthorities.secondaryLicenceAuthorityUniqueId,
      );

      const isAssignedToLegal = account?.assignedLegalIds.includes(id as string);
      const hasViewCasesPermission = hasPermissions([EPermission.VIEW_CASES_LE]);

      const allowedToAddCase =
        includesPrimaryLicenceAuthority ||
        includesSecondaryLicenceAuthority ||
        (hasViewCasesPermission && isAssignedToLegal);

      const hasViewRestrictedCasesPermission = hasPermissions([
        EPermission.VIEW_RESTRICTED_CASES,
      ]);
      const passesRestrictedGate =
        !isNonPublicLegal || hasViewRestrictedCasesPermission === true;

      setCanAddCase((allowedToAddCase ?? false) && passesRestrictedGate);
    }
  }, [legalEntityLicenceAuthorities, account, legalEntity, orgLegalEntity]);

  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<{ processTypeUniqueId: string }>({
    defaultValues: { processTypeUniqueId: '0' },
  });

  const selectedProcessType = watch('processTypeUniqueId');

  useEffect(() => {
    if (!caseProcessTypes) {
      getCaseProcessTypes();
    }
    if (!weaponLicenceCaseStatus) {
      getWeaponLicenceCaseStatus();
    }
  }, [selectedLanguage]);
  useEffect(() => {
    if (caseProcessTypes) {
      const order = new Map(sortedCaseProcessTypesId.map((id, index) => [id, index]));
      const sorted = [...caseProcessTypes].sort((a, b) => {
        const posA = order.get(a.uniqueId as ECaseProcessTypes) ?? Infinity;
        const posB = order.get(b.uniqueId as ECaseProcessTypes) ?? Infinity;
        return posA - posB;
      });
      setCaseProcessTypesOptions(sorted);
    }
  }, [caseProcessTypes]);

  useEffect(() => {
    (async function getLegalEntityCasesInit() {
      if ((!!legalEntity || !!orgLegalEntity) && id && !!selectedProcessType) {
        if (!legalEntityCases.length) setIsLegalEntityCasesLoading(true);
        if (casesSort !== previousCasesSortRef.current) setIsCasesSortLoaded(false);
        setIsCasePaginationLoading(true);
        const processTypeUniqueId = getValues('processTypeUniqueId');

        await getLegalEntityCasesData(
          id,
          currentPageCases,
          perPageCases,
          {
            caseNumber: casesQuery.caseId,
            statusUniqueId: casesQuery.statusUniqueId,
            processTypeUniqueId: processTypeUniqueId === '0' ? '' : processTypeUniqueId,
          },
          casesSort,
        );

        setIsCaseQueryLoading(false);
        setIsLegalEntityCasesLoading(false);
        setIsCasePaginationLoading(false);
        setIsCasesSortLoaded(true);
        previousCasesSortRef.current = casesSort;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    currentPageCases,
    perPageCases,
    casesSort,
    legalEntity,
    orgLegalEntity,
    casesQuery,
    selectedProcessType,
  ]);

  const handleView = (caseId: string) =>
    navigate(
      `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':id', id).replace(':caseId', caseId)}?type=${type}&expand=cases`,
    );

  const handleCreateLicence = (caseId: string) =>
    navigate(
      `${ERouteLinks.LegalEntityAddLicenceByCase.replace(':id', id).replace(':caseId', caseId)}?type=${type}&expand=cases`,
    );

  const handleAssign = (id: string) => setAssignCaseId(id);
  const updateData = async () => {
    const processTypeUniqueId = getValues('processTypeUniqueId');
    await await getLegalEntityCasesData(
      id,
      currentPageCases,
      perPageCases,
      {
        caseNumber: casesQuery.caseId,
        statusUniqueId: casesQuery.statusUniqueId,
        processTypeUniqueId: processTypeUniqueId === '0' ? '' : processTypeUniqueId,
      },
      casesSort,
    );
  };

  return (
    <Grid size={{ xs: 12 }}>
      {assignCaseId && (
        <AssignCaseForm
          isOpen={!!assignCaseId}
          close={() => setAssignCaseId(null)}
          updateData={updateData}
          caseId={assignCaseId}
        />
      )}
      <Accordion
        title={t('legalEntityDetails.cases.accordion.label')}
        name="cases"
        expanded={isAllExpanded}
      >
        <Grid container columnSpacing={2} sx={{ mb: 1, alignItems: 'center' }}>
          <Grid size={{ xs: 12 }}>
            <FormRadioGroup
              name="processTypeUniqueId"
              label={t('legalEntityDetails.cases.display.label')}
              control={control}
              errors={errors}
              options={[
                { label: t('global.all.label'), value: '0' },
                ...mapDictionaryToOptionProp(caseProcessTypesOptions),
              ]}
              oneLine
            />
          </Grid>
        </Grid>

        <CasesSearchForm
          onSubmit={(it: any) => {
            setIsCaseQueryLoading(true);
            setCasesQuery(it);
            setCurrentPageCases(1);
          }}
          isSearchButtonDisabled={isCaseQueryLoading}
        />

        <TableTopButtons>
          {canAddCase && (
            <Button
              id="legal_entity_details_cases-add"
              label={t('legalEntityDetails.cases.add.button')}
              variant={EButtonVariants.contained}
              icon={EIconTypes.plus}
              permission={EPermission.ADD_CASES}
              onClick={() =>
                navigate(
                  `${ERouteLinks.LegalEntityCaseAddPage.replace(':id', id)}?type=${type}&expand=cases`,
                )
              }
              sx={{ ml: 1 }}
            />
          )}
        </TableTopButtons>

        {isLegalEntityCasesLoading ? (
          <Loader isLoading={isLegalEntityCasesLoading} />
        ) : (
          <div>
            {isDesktop ? (
              <Table
                columns={legalEntityCasesTableColumns({
                  isApplication: !!(selectedProcessType === ECaseProcessTypes.APPLICATION),
                  isOtherRequest: !!(selectedProcessType === ECaseProcessTypes.OTHER_REQUEST),
                  handleView,
                  handleCreateLicence,
                  handleAssign,
                  canAssignCase,
                  showDetailsCases,
                  t,
                })}
                dataSource={mapLECasesToDataSource(
                  legalEntityCases,
                  shortDateFormat,
                  weaponLicenceCaseStatus,
                )}
                onSort={(it) => {
                  setCasesSort(it);
                  scrollPosition.current = window.scrollY;
                }}
                specificRowColor={renderRowColor}
                specificRowColorHover={renderRowColorHover}
                isSortLoaded={isCasesSortLoaded}
              />
            ) : (
              <CardList
                items={mapLECasesToDataSource(
                  legalEntityCases,
                  shortDateFormat,
                  weaponLicenceCaseStatus,
                )}
                render={(data, index) => (
                  <Box key={index} sx={{ mb: 2, mt: 2 }}>
                    <LegalEntityCaseTableCard
                      data={data}
                      isApplication={!!(selectedProcessType === ECaseProcessTypes.APPLICATION)}
                      isOtherRequest={
                        !!(selectedProcessType === ECaseProcessTypes.OTHER_REQUEST)
                      }
                      handleView={handleView}
                      handleCreateLicence={handleCreateLicence}
                      handleAssign={handleAssign}
                      key={index}
                      canAssignCase={canAssignCase}
                      showDetailsCases={showDetailsCases}
                    />
                  </Box>
                )}
              />
            )}
          </div>
        )}

        {paginatorCases && (
          <Pagination
            count={paginatorCases.totalElements}
            perPage={perPageCases}
            onChangePage={(page) => setCurrentPageCases(page)}
            onChangePerPage={(value) => setPerPageCases(value)}
            current={currentPageCases}
            isLoading={isCasePaginationLoading}
          />
        )}
      </Accordion>
    </Grid>
  );
};

export default LegalEntityCasesSection;
